import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomerCombobox } from "@/components/customers/CustomerCombobox";
import { Plus } from "lucide-react";
import { WorkEntryTable } from "@/components/work/WorkEntryTable";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkTracking() {
  const [workEntries, setWorkEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const { can } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: "",
    technician_id: "",
    description: "",
    time_spent: 0,
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchWorkEntries();
    fetchCustomers();
    fetchTechnicians();
  }, []);

  const fetchWorkEntries = async () => {
    try {
      const response = await axios.get(`${API}/work-entries`);
      setWorkEntries(response.data);
    } catch (error) {
      toast.error("Failed to fetch work entries");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to fetch customers");
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`);
      setTechnicians(response.data);
    } catch (error) {
      toast.error("Failed to fetch technicians");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await axios.put(`${API}/work-entries/${editingEntry.id}`, formData);
        toast.success("Work entry updated successfully");
      } else {
        await axios.post(`${API}/work-entries`, formData);
        toast.success("Work entry created successfully");
      }
      setOpen(false);
      resetForm();
      fetchWorkEntries();
    } catch (error) {
      toast.error(editingEntry ? "Failed to update work entry" : "Failed to create work entry");
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      customer_id: entry.customer_id,
      technician_id: entry.technician_id || "",
      description: entry.description,
      time_spent: entry.time_spent,
      date: entry.date
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this work entry?")) return;
    try {
      await axios.delete(`${API}/work-entries/${id}`);
      toast.success("Work entry deleted successfully");
      fetchWorkEntries();
    } catch (error) {
      toast.error("Failed to delete work entry");
    }
  };

  const downloadPDF = async (entryId) => {
    try {
      const response = await axios.get(`${API}/work-entries/${entryId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `work-order-${entryId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Work order downloaded successfully");
    } catch (error) {
      toast.error("Failed to download work order");
    }
  };

  const convertToInvoice = async (entryId) => {
    const rate = prompt("Enter hourly rate:", "50");
    if (!rate) return;
    
    try {
      await axios.post(`${API}/work-entries/${entryId}/convert-to-invoice?hourly_rate=${parseFloat(rate)}`);
      toast.success("Work order converted to invoice successfully");
      fetchWorkEntries();
    } catch (error) {
      toast.error("Failed to convert to invoice");
    }
  };

  const bulkConvert = async (selected, clearSelection) => {
    const rate = prompt(`Enter hourly rate for ${selected.length} entries (${selected.reduce((s, e) => s + e.time_spent, 0).toFixed(1)} h):`, "50");
    if (!rate) return;
    try {
      const { data } = await axios.post(`${API}/work-entries/bulk-convert-to-invoice`, { entry_ids: selected.map((e) => e.id), hourly_rate: parseFloat(rate) });
      toast.success(`Created ${data.invoice_number} for $${data.total.toFixed(2)} from ${selected.length} work entries`);
      clearSelection();
      fetchWorkEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    }
  };

  const emailWorkOrder = async (entryId) => {
    try {
      const response = await axios.post(`${API}/work-entries/${entryId}/email`);
      toast.info(response.data.message);
    } catch (error) {
      toast.error("Failed to send email");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      technician_id: "",
      description: "",
      time_spent: 0,
      date: new Date().toISOString().split('T')[0]
    });
    setEditingEntry(null);
  };

  return (
    <div data-testid="work-tracking-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Work Tracking</h1>
          <p className="text-slate-600">Track time and work done for customers</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className={`bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg ${can("work.create") ? "" : "hidden"}`} data-testid="add-work-entry-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Work Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingEntry ? "Edit Work Entry" : "Add New Work Entry"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="customer">Customer *</Label>
                <CustomerCombobox
                  customers={customers}
                  value={formData.customer_id}
                  onChange={(value) => setFormData({ ...formData, customer_id: value })}
                  onCreated={(c) => setCustomers((prev) => [...prev, c])}
                  allowCreate
                  testId="customer-select"
                />
              </div>
              <div>
                <Label htmlFor="technician">Technician *</Label>
                <Select value={formData.technician_id} onValueChange={(value) => setFormData({ ...formData, technician_id: value })} required>
                  <SelectTrigger data-testid="technician-select">
                    <SelectValue placeholder="Select technician" />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.map((technician) => (
                      <SelectItem key={technician.id} value={technician.id}>{technician.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="time_spent">Time Spent (hours) *</Label>
                <Input
                  id="time_spent"
                  type="number"
                  data-testid="time-spent-input"
                  value={formData.time_spent}
                  onChange={(e) => setFormData({ ...formData, time_spent: parseFloat(e.target.value) || 0 })}
                  required
                  min="0"
                  step="0.5"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="description">Work Description *</Label>
                <Textarea
                  id="description"
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="mt-1"
                  rows={4}
                  placeholder="Describe the work completed..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="save-work-entry-button">
                  {editingEntry ? "Update" : "Create"}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <WorkEntryTable
          entries={workEntries}
          customers={customers}
          technicians={technicians}
          currentUser={currentUser}
          actions={{ downloadPDF, convertToInvoice, emailWorkOrder, bulkConvert, onEdit: handleEdit, onDelete: handleDelete }}
        />
      )}
    </div>
  );
}
